import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Plus, Trash2, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface ConsignmentItemForm {
  productId: string;
  productName: string;
  quantitySent: number;
  unitCost: number;
  sellingPrice: number;
  currency: string;
  notes?: string;
}

export function NewConsignmentPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');

  const [vendorId, setVendorId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [commissionRate, setCommissionRate] = useState<number | ''>('');
  const [expectedReturn, setExpectedReturn] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<ConsignmentItemForm[]>([]);

  // Product selection
  const [selectedProductId, setSelectedProductId] = useState('');

  // Fetch data
  const { data: vendorsData } = trpc.tradingConsignment.listVendors.useQuery();
  const { data: warehousesData } = trpc.tradingConsignment.listWarehouses.useQuery();
  const { data: productsData } = trpc.tradingProduct.list.useQuery({});

  const vendors = vendorsData || [];
  const warehouses = warehousesData || [];
  const products = productsData?.products || [];

  const vendorOptions = vendors.map((v) => ({
    value: v.id,
    label: v.companyName,
  }));

  const warehouseOptions = warehouses.map((w) => ({
    value: w.id,
    label: `${w.code} - ${w.name}`,
  }));

  const productOptions = products
    .filter((p) => !items.some((item) => item.productId === p.id))
    .map((p) => ({
      value: p.id,
      label: `${p.name} (${p.model.brand.name})`,
    }));

  const addItem = () => {
    if (!selectedProductId) return;

    const product = products.find((p) => p.id === selectedProductId);
    if (!product) return;

    // Consignment prefill: unitCost = the dealer/vendor price (what the
    // vendor owes us per unit — commercial reference only; inventory
    // valuation and movements use the real FIFO cost server-side), and
    // sellingPrice = the retail price the vendor sells at. Both editable.
    const vendorPrice = (product as any).vendorPrice;
    setItems([
      ...items,
      {
        productId: product.id,
        productName: `${product.name} (${product.model.brand.name})`,
        quantitySent: 1,
        unitCost: Number(vendorPrice ?? product.price) || 0,
        sellingPrice: Number(product.price) || 0,
        currency: (product as any).vendorCurrency || product.currency || 'EUR',
      },
    ]);
    setSelectedProductId('');
  };

  const updateItem = (index: number, field: keyof ConsignmentItemForm, value: unknown) => {
    const newItems = [...items];
    const existingItem = newItems[index];
    if (existingItem) {
      newItems[index] = { ...existingItem, [field]: value } as ConsignmentItemForm;
      setItems(newItems);
    }
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const createMutation = trpc.tradingConsignment.create.useMutation({
    onSuccess: (data) => {
      showSuccessToast(tCommon('created'));
      navigate(`/trading/consignment/${data.id}`);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const handleSubmit = () => {
    if (!vendorId || !warehouseId || items.length === 0) {
      showErrorToast('Please fill in all required fields');
      return;
    }

    createMutation.mutate({
      vendorId,
      warehouseId,
      commissionRate: commissionRate !== '' ? commissionRate : undefined,
      expectedReturn: expectedReturn ? new Date(expectedReturn) : undefined,
      notes: notes || undefined,
      items: items.map((item) => ({
        productId: item.productId,
        quantitySent: item.quantitySent,
        unitCost: item.unitCost,
        sellingPrice: item.sellingPrice,
        currency: item.currency,
        notes: item.notes,
      })),
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/trading/consignment')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{t('consignment.form.title')}</h1>
          <p className="text-muted-foreground">{t('consignment.subtitle')}</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Vendor & Warehouse */}
          <Card>
            <CardHeader>
              <CardTitle>{t('consignment.detail.overview')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t('consignment.form.selectVendor')} *</Label>
                  <Combobox
                    options={vendorOptions}
                    value={vendorId}
                    onValueChange={setVendorId}
                    placeholder={t('consignment.list.selectVendor')}
                    searchPlaceholder={tCommon('search')}
                    emptyText={t('consignment.vendors.noVendors')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('consignment.form.selectWarehouse')} *</Label>
                  <Combobox
                    options={warehouseOptions}
                    value={warehouseId}
                    onValueChange={setWarehouseId}
                    placeholder={t('consignment.form.selectWarehouse')}
                    searchPlaceholder={tCommon('search')}
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t('consignment.form.commissionRate')}</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    value={commissionRate}
                    onChange={(e) => setCommissionRate(e.target.value ? parseFloat(e.target.value) : '')}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('consignment.form.expectedReturn')}</Label>
                  <Input
                    type="date"
                    value={expectedReturn}
                    onChange={(e) => setExpectedReturn(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('consignment.form.notes')}</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <Card>
            <CardHeader>
              <CardTitle>{t('consignment.form.addItems')}</CardTitle>
              <CardDescription>{t('consignment.detail.items')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add Item */}
              <div className="flex gap-2">
                <div className="flex-1">
                  <Combobox
                    options={productOptions}
                    value={selectedProductId}
                    onValueChange={setSelectedProductId}
                    placeholder={t('consignment.form.product')}
                    searchPlaceholder={tCommon('search')}
                  />
                </div>
                <Button onClick={addItem} disabled={!selectedProductId}>
                  <Plus className="h-4 w-4 mr-2" />
                  {tCommon('add')}
                </Button>
              </div>

              {/* Items List */}
              {items.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="mx-auto h-12 w-12 text-muted-foreground/50" />
                  <p className="mt-2">{t('consignment.form.noProducts')}</p>
                </div>
              ) : (
                <div className="border border-border divide-y">
                  {items.map((item, index) => (
                    <div key={item.productId} className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <span className="font-medium">{item.productName}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem(index)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="grid gap-4 md:grid-cols-4">
                        <div className="space-y-1">
                          <Label className="text-xs">{t('consignment.form.quantity')}</Label>
                          <Input
                            type="number"
                            min={1}
                            value={item.quantitySent}
                            onChange={(e) => updateItem(index, 'quantitySent', parseInt(e.target.value) || 1)}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">{t('consignment.form.unitCost')}</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min={0}
                            value={item.unitCost}
                            onChange={(e) => updateItem(index, 'unitCost', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">{t('consignment.form.sellingPrice')}</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min={0}
                            value={item.sellingPrice}
                            onChange={(e) => updateItem(index, 'sellingPrice', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">{tCommon('total')}</Label>
                          <div className="h-8 flex items-center px-3 border border-border bg-muted text-sm">
                            {(item.quantitySent * item.unitCost).toFixed(2)} {item.currency}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{tCommon('summary')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('consignment.detail.items')}:</span>
                <span>{items.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('consignment.form.quantity')}:</span>
                <span>{items.reduce((sum, item) => sum + item.quantitySent, 0)}</span>
              </div>
              <div className="border-t pt-3">
                <div className="flex justify-between font-medium">
                  <span>{t('consignment.detail.totalValue')}:</span>
                  <span>
                    {items.reduce((sum, item) => sum + item.quantitySent * item.unitCost, 0).toFixed(2)}{' '}
                    {items[0]?.currency || 'EUR'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Button
            className="w-full"
            size="lg"
            onClick={handleSubmit}
            disabled={!vendorId || !warehouseId || items.length === 0 || createMutation.isPending}
          >
            {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {tCommon('create')}
          </Button>
        </div>
      </div>
    </div>
  );
}
